import { _ } from "../polyglot/polyglot.js";
import { guid, stripBom } from "../helpers/string-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import jQuery from "jquery";

/**
 * Widget displaying a button that, when clicked, opens a file
 * selection dialog and executes a provided callback with the file
 * content as parameter.
 *
 * Static methods exist for configuring an instance to read a file depending on
 * its type: JSON, data URI or binary. These methods set the `readFile` and
 * `transformData` constructor arguments.
 *
 * @param {Renderable} [spec.label] - Content of the button.
 * @param {string} [spec.cssClass] - Additional CSS classes for the button.
 * @param {string} dataHelpName - String used by the help system to find the button.
 * @param {string} [dataTestName] - String used as data-test attribute
 * to facilitate UI testing.
 * @param {function} spec.fileSelectedCallback - Function to execute with the
 * file's content as argument.
 * @param {function} [spec.readFile] - Function used to read data. It takes a
 * FileReader instance and a File object.
 * @param {function} [spec.transformData] - Function used to transform data
 * before sending it to the `fileSelectedCallback`. No-op by default.
 */
export default class FileSelectionButtonWidget extends Widget {
	constructor({
		label,
		cssClass = "btn",
		dataHelpName,
		dataTestName = "select-file",
		fileSelectedCallback,
		readFile,
		transformData = (data) => data,
	}) {
		super(...arguments);

		this._label = label || _("Choose file");
		this._cssClass = cssClass;
		this._dataHelpName = dataHelpName;
		this._dataTestName = dataTestName;

		this._fileSelectedCallback = fileSelectedCallback;
		this._readFile = readFile;
		this._transformData = transformData;
	}

	setLabel(label) {
		this._label = label;
		this.update();
	}

	makeDisabled() {
		this.asJQuery().addClass("disabled");
	}

	makeEnabled() {
		this.asJQuery().removeClass("disabled");
	}

	renderContentOn(html) {
		html.span(this._label);
	}

	_renderRootOn(html) {
		return html.a({
			klass: this._cssClass,
			"data-test": this._dataTestName,
			"data-help": this._dataHelpName,
			id: this.getId(),
			click: this._openFileSelectionDialog.bind(this),
		});
	}

	_openFileSelectionDialog() {
		this._importInput = jQuery(
			'<input type="file" style="display: none;"></input>',
		);

		jQuery("body").append(this._importInput);

		this._importInput.change((event) => {
			this._selectFile(event.target.files[0]);
			this._importInput.remove();
		});

		this._importInput.click();
	}

	_dispose() {
		if (!this._importInput) {
			return;
		}
		this._importInput.remove();
	}

	_selectFile(file) {
		if (!this._readFile) {
			this._fileSelectedCallback(file);
			return;
		}

		const reader = new FileReader();

		reader.onload = () =>
			this._fileSelectedCallback(this._transformData(reader.result));

		this._readFile(reader, file);
	}

	static forFormData(spec) {
		return new this(spec);
	}

	static forJSONFile(spec) {
		spec = {
			...spec,
			readFile: (reader, file) => reader.readAsText(file, "utf-8"),
			transformData: this.transformJSON.bind(this),
		};

		return new this(spec);
	}

	static forDataURL(spec) {
		spec = {
			...spec,
			readFile: (reader, file) => reader.readAsDataURL(file),
		};

		return new this(spec);
	}

	static transformJSON(data) {
		const object = JSON.parse(stripBom(data));

		// We override the `id` to avoid conflicts on the
		// server-side in case the file being imported was already
		// on the server:
		object.id = guid();

		return object;
	}
}
